import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

// English is the fallback language and stays statically bundled: every key
// renders immediately on first paint, worst case in English. The other locales
// load as webpack chunks on demand (see resourcesToBackend below), which keeps
// ~800 KB of de/fr/it JSON out of the entry bundle. react-i18next re-renders
// when a locale chunk arrives (`useSuspense: false` binds to the loaded event).
import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enTenant from '../locales/en/tenant.json';
import enOrders from '../locales/en/orders.json';
import enEmails from '../locales/en/emails.json';
import enSettings from '../locales/en/settings.json';
import enNavigation from '../locales/en/navigation.json';
import enErrors from '../locales/en/errors.json';
import enPortal from '../locales/en/portal.json';
import enChat from '../locales/en/chat.json';
import enHitlReview from '../locales/en/hitlReview.json';
import enLogs from '../locales/en/logs.json';

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    tenant: enTenant,
    orders: enOrders,
    emails: enEmails,
    settings: enSettings,
    navigation: enNavigation,
    errors: enErrors,
    portal: enPortal,
    chat: enChat,
    hitlReview: enHitlReview,
    logs: enLogs,
  },
};

i18n
  .use(LanguageDetector)
  // The template-literal import() becomes a webpack context: one lazy chunk per
  // locale JSON file, fetched the first time a (language, namespace) pair is
  // needed. English never hits this backend — it is bundled above.
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`../locales/${language}/${namespace}.json`),
    ),
  )
  .use(initReactI18next)
  .init({
    resources,
    // `resources` holds only English; tell i18next to still consult the backend
    // for everything else instead of treating the bundle as complete.
    partialBundledLanguages: true,
    fallbackLng: 'en',
    // Only base language codes have locale files/chunks. A detected regional
    // code (navigator.language 'de-DE' on a first visit) must resolve to 'de' —
    // otherwise the backend would request locales/de-DE/*.json, a chunk that
    // doesn't exist, and the user would silently stay on English.
    supportedLngs: ['en', 'de', 'fr', 'it'],
    nonExplicitSupportedLngs: true, // de-DE counts as supported via de
    load: 'languageOnly', // and only de is actually loaded
    defaultNS: 'common',
    ns: ['common', 'auth', 'tenant', 'orders', 'emails', 'settings', 'navigation', 'errors', 'portal', 'chat', 'hitlReview', 'logs'],
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    react: {
      useSuspense: false, // Disable suspense to avoid issues
    },
    // Development-only: warn when a runtime lookup falls back to English so
    // missing locale keys surface while clicking through the UI.
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lngs, ns, key) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[i18n] missing key ${lngs.join(',')}/${ns}:${key}`);
      }
    },
  })
  .then(() => {
    // Kick off loading the resolved language's chunks right away (instead of on
    // first t() miss) to keep the English flash on de/fr/it first loads short.
    // resolvedLanguage (post-init, supportedLngs applied) is 'de', never 'de-DE'.
    const lng = i18n.resolvedLanguage;
    if (lng && lng !== 'en') {
      i18n.loadLanguages(lng);
    }
  });

if (process.env.NODE_ENV === 'development') {
  console.log('[i18n] Initialized with language:', i18n.language);
  i18n.on('languageChanged', (lng) => {
    console.log('[i18n] Language changed to:', lng);
  });
}

export default i18n;
